import { AxiosResponse } from 'axios';
import { AuthError, getAuth, signOut } from 'firebase/auth';
import useUserStore from 'store/useUserStore';
import {
  clearPostLoginRedirect,
  setSuppressNextPostLoginPreserve,
} from 'utils/postLoginRedirect';
import { openErrorToaster } from './toast.service';

const ERROR_TOAST_TIMER = 10000;

/** Blob responses (e.g. PDF download) may carry JSON error bodies as a Blob — parse for `message`. */
const PERMISSION_OR_SESSION_FALLBACK =
  'You do not have permission to perform this action, or your session is no longer valid.';

function genericHttpFallback(status: number, statusText?: string): string {
  const st = statusText?.trim();
  if (st) return st;
  return `Something went wrong with this request (HTTP ${status}).`;
}

async function extractAxiosErrorMessage(
  response: AxiosResponse<unknown>,
): Promise<string> {
  const { data, status, statusText } = response;

  if (data instanceof Blob) {
    try {
      const text = (await data.text()).trim();
      if (text.startsWith('{')) {
        try {
          const parsed = JSON.parse(text) as { message?: unknown };
          if (typeof parsed.message === 'string' && parsed.message.trim()) {
            return parsed.message.trim();
          }
        } catch {
          // not JSON
        }
      }
    } catch {
      // blob read failed
    }
    if (status === 403 || status === 401) {
      return PERMISSION_OR_SESSION_FALLBACK;
    }
    return genericHttpFallback(status, statusText);
  }

  if (data && typeof data === 'object' && data !== null && 'message' in data) {
    const msg = (data as { message?: unknown }).message;
    if (typeof msg === 'string' && msg.trim()) {
      return msg.trim();
    }
  }

  if (typeof data === 'string' && data.trim()) {
    return data.trim();
  }

  if (status === 403 || status === 401) {
    return PERMISSION_OR_SESSION_FALLBACK;
  }

  return genericHttpFallback(status, statusText);
}

function defaultErrorHandler() {
  // show default error alert
  openErrorToaster(
    'Server Error. Please refresh the page and try again.',
    ERROR_TOAST_TIMER,
  );
}

/**
 * Clears user session and logs user out
 * @param message
 * @param logout
 */
async function handleInvalidSession(message: string) {
  try {
    clearPostLoginRedirect();
    setSuppressNextPostLoginPreserve();
    // get user session
    const auth = getAuth();
    // sign out user
    await signOut(auth);
    // clear user session
    useUserStore.getState().logout();
    // show error alert
    openErrorToaster(
      message.trim() || PERMISSION_OR_SESSION_FALLBACK,
      ERROR_TOAST_TIMER,
    );
  } catch (e) {
    openErrorToaster(
      'An error occurred. Please refresh the page and try again.',
      ERROR_TOAST_TIMER,
    );
  }
}

async function axiosServerErrorHandler(response: AxiosResponse<unknown>) {
  const message = await extractAxiosErrorMessage(response);
  if (response.status === 403) {
    await handleInvalidSession(message);
  } else if (response.status === 404) {
    openErrorToaster(
      message.startsWith('Something went wrong with this request')
        ? 'The requested resource was not found.'
        : message,
      ERROR_TOAST_TIMER,
    );
  } else {
    openErrorToaster(message, ERROR_TOAST_TIMER);
  }
}

function firebaseAuthErrorHandler(error: AuthError) {
  const authError = error as AuthError;
  const errorCode = authError.code;

  switch (errorCode) {
    case 'auth/too-many-requests':
      openErrorToaster(
        'Too many failed attempts. Try again in a few minutes, or reset your password.',
        ERROR_TOAST_TIMER,
      );
      break;
    case 'auth/invalid-credential':
      openErrorToaster(
        'Wrong email or password. If you signed up with Google, use Sign in with Google.',
        ERROR_TOAST_TIMER,
      );
      break;
    case 'auth/user-not-found':
      // Same message as a wrong password, so login doesn't reveal
      // which emails have accounts.
      openErrorToaster(
        'Wrong email or password. If you signed up with Google, use Sign in with Google.',
        ERROR_TOAST_TIMER,
      );
      break;
    case 'auth/wrong-password':
      openErrorToaster(
        'Wrong email or password. If you signed up with Google, use Sign in with Google.',
        ERROR_TOAST_TIMER,
      );
      break;
    case 'auth/email-already-in-use':
      openErrorToaster(
        'An account with that email already exists.',
        ERROR_TOAST_TIMER,
      );
      break;

    case 'auth/weak-password':
      openErrorToaster(
        'Password must be at least 6 characters long.',
        ERROR_TOAST_TIMER,
      );
      break;

    case 'auth/user-token-expired':
      openErrorToaster(
        'Your session expired. Please log in again.',
        ERROR_TOAST_TIMER,
      );
      useUserStore.getState().logout(); // clear user session data
      break;

    case 'auth/invalid-user-token':
      openErrorToaster(
        'Your session expired. Please log in again.',
        ERROR_TOAST_TIMER,
      );
      useUserStore.getState().logout(); // clear user session data
      break;

    case 'auth/expired-action-code':
    case 'auth/invalid-action-code':
      openErrorToaster(
        'This link has expired or was already used. Request a new one from Forgot password.',
        ERROR_TOAST_TIMER,
      );
      break;

    default:
      defaultErrorHandler();
      break;
  }
}

/**
 * This method handles server errors. An danger alert is
 * displayed with relevant info
 * @param error
 * @param logout
 */
const serverErrorHandler = (error: any) => {
  if (error.code) {
    firebaseAuthErrorHandler(error);
  } else if (error.response) {
    axiosServerErrorHandler(error.response).catch(() => defaultErrorHandler());
  } else {
    // Not a server or Firebase error: log it so the cause is visible.
    // eslint-disable-next-line no-console
    console.error('Unexpected error', error);
    defaultErrorHandler();
  }
};

export default serverErrorHandler;
